// FOUNDRY TITAN part textures: a hovering crane-forge. Hull, thrusters,
// hanging crucible (with its armoured heart and sliding lids), crane boom +
// grapple claw, forge hammer on a telescoping piston, and the phase-3
// flamethrower nozzle are separate textures animated by the rig.

import type Phaser from 'phaser';
import { COLORS } from '../../config/palette';
import {
  artRng,
  bevelEdges,
  bounds,
  breach,
  canvasTex,
  chamfer,
  cracks,
  css,
  dents,
  disc,
  ellipse,
  emissive,
  emissiveDot,
  glowMask,
  groove,
  hub,
  mix,
  poly,
  recess,
  rivets,
  scorch,
  shade,
  softDot,
  solid,
  sphere,
  stencil,
  stripes,
  vents,
  type G,
  type Pt,
} from './artkit';
import type { Dmg } from './art-warden';

export const TITAN_TEX = {
  hull: (d: Dmg) => `boss_titan_hull_${d}`,
  hullGlow: 'boss_titan_hull_glow',
  thruster: 'boss_titan_thruster',
  flame: 'boss_titan_flame',
  crucible: (d: Dmg) => `boss_titan_crucible_${d}`,
  crucibleGlow: 'boss_titan_crucible_glow',
  heart: 'boss_titan_heart',
  heartGlow: 'boss_titan_heart_glow',
  lidL: 'boss_titan_lid_l',
  lidR: 'boss_titan_lid_r',
  boom: 'boss_titan_boom',
  hook: 'boss_titan_hook',
  chain: 'boss_titan_chain',
  piston: 'boss_titan_piston',
  hammer: 'boss_titan_hammer',
  hammerGlow: 'boss_titan_hammer_glow',
  nozzle: 'boss_titan_nozzle',
} as const;

/** Rig geometry, facing right, y down, relative to the hull centre unless noted. */
export const TITAN_RIG = {
  thrusterRear: { x: -122, y: 66 },
  thrusterFront: { x: 116, y: 66 },
  /** Flame mount relative to a thruster pivot. */
  flameMount: { x: 0, y: 48 },
  cruciblePivot: { x: -6, y: 62 },
  /** In crucible space (relative to the hanging pivot). */
  spout: { x: 96, y: 16 },
  heart: { x: 0, y: 90 },
  lidTravel: 32,
  boomMount: { x: 58, y: -46 },
  boomLen: 180,
  /** Claw grab point relative to the hook pivot. */
  hookGrab: { x: 0, y: 50 },
  pistonMount: { x: 128, y: 74 },
  pistonRest: 34,
  /** Hammer striking face relative to the hammer pivot. */
  hammerFace: 74,
  nozzleMount: { x: -76, y: 72 },
  nozzleLen: 58,
  stacks: [
    { x: -117, y: -100 },
    { x: -81, y: -92 },
  ],
  eyes: { x: 140, y: -2 },
  tex: {
    hull: { w: 388, h: 200, ox: 194, oy: 106 },
    thruster: { w: 80, h: 64, ox: 40, oy: 8 },
    flame: { w: 64, h: 136, ox: 32, oy: 8 },
    crucible: { w: 200, h: 180, ox: 100, oy: 14 },
    heart: { w: 80, h: 80, ox: 40, oy: 40 },
    heartGlow: { w: 220, h: 220, ox: 110, oy: 110 },
    lidL: { w: 56, h: 88, ox: 53, oy: 44 },
    lidR: { w: 56, h: 88, ox: 3, oy: 44 },
    boom: { w: 214, h: 48, ox: 16, oy: 24 },
    hook: { w: 70, h: 82, ox: 35, oy: 8 },
    chain: { w: 16, h: 24 },
    piston: { w: 32, h: 48 },
    hammer: { w: 148, h: 92, ox: 74, oy: 12 },
    nozzle: { w: 80, h: 44, ox: 14, oy: 22 },
  },
} as const;

const P = {
  hull: 0x5d4c3f,
  plate: 0x7d6a56,
  frame: 0x2c2420,
  joint: 0x3e3530,
  paint: 0xd99a2b,
  stripe: 0x17140f,
  steel: 0x77818f,
  molten: 0xff7a1a,
  moltenHot: 0xffcf4a,
};

// ───────────── hull ─────────────

const HULL_SHAPE: Pt[] = [
  [24, 128],
  [18, 94],
  [42, 64],
  [120, 54],
  [152, 38],
  [256, 36],
  [306, 48],
  [356, 70],
  [376, 104],
  [362, 140],
  [306, 160],
  [94, 162],
  [44, 152],
];

function drawStack(g: G, x: number, y: number, w: number, h: number): void {
  const body = chamfer(x, y, w, h, [3, 3, 0, 0]);
  solid(g, body, { x, y, w, h }, P.joint, { bevel: 2 });
  const rim = chamfer(x - 3, y - 2, w + 6, 9, 2);
  solid(g, rim, { x: x - 3, y: y - 2, w: w + 6, h: 9 }, P.frame, { bevel: 1.5 });
  recess(g, ellipse(x + w / 2, y + 1, w / 2 - 2, 3), 0x050404, 1);
  softDot(g, x + w / 2, y + 1, w * 0.45, P.molten, 0.45);
  groove(g, [
    [x + 3, y + h * 0.45],
    [x + w - 3, y + h * 0.45],
  ], P.joint);
}

function drawHull(g: G, d: Dmg): void {
  const rng = artRng(`titan-hull-${d}`);
  drawStack(g, 62, 6, 30, 64);
  drawStack(g, 100, 16, 26, 52);

  const shell = poly(HULL_SHAPE);
  solid(g, shell, bounds(HULL_SHAPE), P.hull, { bevel: 3, hi: 1.45 });

  // Upper deck armour.
  const deck: Pt[] = [
    [46, 66],
    [122, 58],
    [154, 44],
    [254, 42],
    [302, 54],
    [342, 72],
    [300, 82],
    [62, 84],
  ];
  solid(g, poly(deck), bounds(deck), P.plate, { bevel: 2.5, hi: 1.35 });
  groove(g, [
    [70, 76],
    [296, 74],
  ], P.plate);
  stencil(g, 'FT-02', 204, 60, 15, P.stripe, 0.8);
  rivets(g, [
    [58, 72],
    [126, 64],
    [160, 50],
    [248, 48],
    [296, 60],
    [330, 74],
  ], P.plate, 2);

  // Crane mount block.
  const mount = chamfer(232, 40, 40, 26, 4);
  solid(g, mount, { x: 232, y: 40, w: 40, h: 26 }, P.frame, { bevel: 1.5 });

  // Furnace side grille (heat glow).
  const grille = chamfer(160, 96, 108, 36, 4);
  recess(g, grille, 0x0a0705, 3);
  g.save();
  g.clip(grille);
  softDot(g, 214, 118, 70, P.molten, 0.8);
  softDot(g, 214, 118, 32, P.moltenHot, 0.7);
  g.restore();
  for (let x = 166; x < 266; x += 12) {
    const slat = chamfer(x, 96, 6, 36, 1);
    solid(g, slat, { x, y: 96, w: 6, h: 36 }, P.joint, { bevel: 1, outline: false });
  }
  g.strokeStyle = css(COLORS.ink);
  g.lineWidth = 2;
  g.stroke(grille);

  // Front visor band with three furnace eyes.
  const visor = poly([
    [304, 86],
    [366, 98],
    [360, 122],
    [306, 114],
  ]);
  recess(g, visor, 0x080605, 3);
  for (let i = 0; i < 3; i++) {
    const x = 314 + i * 15;
    const eye = poly([
      [x, 96 + i * 2],
      [x + 10, 98 + i * 2],
      [x + 9, 108 + i * 2],
      [x, 106 + i * 2],
    ]);
    emissive(g, eye, { x, y: 96 + i * 2, w: 10, h: 12 }, P.molten, { blur: 6 });
  }

  // Rear vents.
  vents(g, 30, 96, 26, 34, 4, P.hull);

  // Hazard lip along the belly.
  const lip = poly([
    [58, 142],
    [328, 142],
    [306, 160],
    [94, 162],
  ]);
  stripes(g, lip, { x: 58, y: 142, w: 270, h: 20 }, P.paint, P.stripe, 9);
  bevelEdges(g, lip, P.paint, 1.5);
  g.strokeStyle = css(COLORS.ink);
  g.lineWidth = 2;
  g.stroke(lip);

  // Crucible bracket underneath.
  const bracket = chamfer(166, 150, 44, 24, [2, 2, 8, 8]);
  solid(g, bracket, { x: 166, y: 150, w: 44, h: 24 }, P.frame, { bevel: 1.5 });
  hub(g, 188, 168, 8, P.joint, 0);

  groove(g, [
    [26, 92],
    [60, 90],
    [150, 90],
  ], P.hull);
  groove(g, [
    [276, 92],
    [300, 136],
  ], P.hull);

  if (d >= 1) {
    cracks(g, rng, 110, 70, 30, { branches: 3, base: P.plate });
    scorch(g, 300, 66, 26, 0.55);
    scorch(g, 70, 120, 22, 0.5);
    dents(g, rng, { x: 40, y: 56, w: 300, h: 90 }, 16, P.hull);
  }
  if (d >= 2) {
    const hole = poly([
      [272, 88],
      [300, 84],
      [306, 118],
      [288, 132],
      [268, 120],
    ]);
    breach(g, rng, hole, { x: 268, y: 84, w: 38, h: 48 }, P.hull, P.molten);
    cracks(g, rng, 140, 120, 34, { branches: 3, glow: P.molten, width: 1.8 });
    cracks(g, rng, 236, 50, 18, { branches: 2, glow: P.molten, width: 1.4 });
  }
}

function drawHullGlow(g: G): void {
  for (let i = 0; i < 3; i++) {
    const x = 314 + i * 15;
    glowMask(g, poly([
      [x, 96 + i * 2],
      [x + 10, 98 + i * 2],
      [x + 9, 108 + i * 2],
      [x, 106 + i * 2],
    ]), 10);
  }
  glowMask(g, chamfer(166, 102, 96, 24, 6), 16, 0.35);
  glowMask(g, ellipse(77, 7, 12, 3), 10, 0.6);
  glowMask(g, ellipse(113, 17, 10, 3), 10, 0.6);
}

// ───────────── thrusters ─────────────

function drawThruster(g: G): void {
  const bell = poly([
    [20, 30],
    [60, 30],
    [68, 58],
    [12, 58],
  ]);
  solid(g, bell, { x: 12, y: 30, w: 56, h: 28 }, P.joint, { bevel: 2 });
  recess(g, ellipse(40, 57, 24, 5), 0x0a0605, 2);
  softDot(g, 40, 57, 24, P.moltenHot, 0.85);
  const pod = chamfer(10, 2, 60, 32, [8, 8, 4, 4]);
  solid(g, pod, { x: 10, y: 2, w: 60, h: 32 }, P.hull, { bevel: 2.5 });
  stripes(g, chamfer(16, 20, 48, 8, 1), { x: 16, y: 20, w: 48, h: 8 }, P.paint, P.stripe, 5);
  rivets(g, [
    [18, 10],
    [62, 10],
  ], P.hull, 1.8);
}

function drawFlame(g: G): void {
  const p = new Path2D();
  p.moveTo(32, 6);
  p.bezierCurveTo(56, 22, 50, 70, 32, 132);
  p.bezierCurveTo(14, 70, 8, 22, 32, 6);
  p.closePath();
  glowMask(g, p, 12, 0.75);
  const core = new Path2D();
  core.moveTo(32, 8);
  core.bezierCurveTo(42, 20, 40, 50, 32, 84);
  core.bezierCurveTo(24, 50, 22, 20, 32, 8);
  core.closePath();
  g.fillStyle = '#ffffff';
  g.fill(core);
}

// ───────────── crucible ─────────────

const BUCKET: Pt[] = [
  [26, 48],
  [174, 48],
  [158, 160],
  [42, 160],
];

function drawCrucible(g: G, d: Dmg): void {
  const rng = artRng(`titan-crucible-${d}`);
  // Hanging bail.
  g.lineCap = 'round';
  for (const end of [
    [28, 64],
    [172, 64],
  ] as Pt[]) {
    g.strokeStyle = css(COLORS.ink);
    g.lineWidth = 10;
    g.beginPath();
    g.moveTo(100, 12);
    g.lineTo(end[0], end[1]);
    g.stroke();
    g.strokeStyle = css(P.joint);
    g.lineWidth = 6;
    g.stroke();
    g.strokeStyle = css(shade(P.joint, 1.8), 0.5);
    g.lineWidth = 1.5;
    g.stroke();
  }
  // Spout (behind the rim).
  const spout: Pt[] = [
    [168, 40],
    [198, 30],
    [194, 46],
    [170, 56],
  ];
  solid(g, poly(spout), bounds(spout), P.frame, { bevel: 1.5 });
  emissive(g, poly([
    [178, 38],
    [196, 32],
    [194, 37],
    [180, 42],
  ]), { x: 178, y: 32, w: 18, h: 10 }, P.moltenHot, { blur: 5 });

  // Bucket with heat gradient towards the bottom.
  const bucket = poly(BUCKET);
  solid(g, bucket, bounds(BUCKET), 0x5a4436, { bevel: 3, hi: 1.4, lo: 0.5 });
  g.save();
  g.clip(bucket);
  const heat = g.createLinearGradient(0, 90, 0, 160);
  heat.addColorStop(0, 'rgba(255,90,20,0)');
  heat.addColorStop(1, 'rgba(255,90,20,0.28)');
  g.fillStyle = heat;
  g.fillRect(20, 90, 160, 72);
  g.restore();
  // Reinforcement bands.
  for (const y of [74, 134]) {
    const w0 = 26 + ((y - 48) / 112) * 16;
    const band = poly([
      [w0 - 2, y],
      [200 - w0 + 2, y],
      [200 - w0, y + 9],
      [w0, y + 9],
    ]);
    solid(g, band, { x: w0, y, w: 200 - 2 * w0, h: 9 }, P.frame, { bevel: 1.5, outline: false });
    const pts: Pt[] = [];
    for (let x = w0 + 10; x < 200 - w0 - 6; x += 22) pts.push([x, y + 4.5]);
    rivets(g, pts, P.frame, 1.6);
  }
  // Glowing seams.
  cracks(g, rng, 52, 110, 18, { branches: 2, glow: P.molten, width: 1.4, angle: 1.2 });
  cracks(g, rng, 152, 100, 16, { branches: 2, glow: P.molten, width: 1.4, angle: 2.2 });

  // Rim and molten surface.
  const rim = chamfer(16, 36, 168, 18, 4);
  solid(g, rim, { x: 16, y: 36, w: 168, h: 18 }, P.frame, { bevel: 2 });
  const surface = ellipse(100, 41, 74, 6);
  emissive(g, surface, { x: 26, y: 35, w: 148, h: 12 }, P.molten, { blur: 10, heat: 0.85 });

  // Trunnions.
  hub(g, 28, 64, 10, P.joint, 4);
  hub(g, 172, 64, 10, P.joint, 4);
  hub(g, 100, 12, 9, P.joint, 0);

  // Heart housing (heart sprite sits at (100, 104)).
  const housing = disc(100, 104, 40);
  solid(g, housing, { x: 60, y: 64, w: 80, h: 80 }, P.frame, { bevel: 3 });
  recess(g, disc(100, 104, 31), 0x0b0604, 4);
  softDot(g, 100, 104, 30, P.molten, 0.35);
  const bolts: Pt[] = [];
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    bolts.push([100 + Math.cos(a) * 35.5, 104 + Math.sin(a) * 35.5]);
  }
  rivets(g, bolts, P.frame, 1.8);

  if (d >= 1) dents(g, rng, { x: 40, y: 60, w: 120, h: 90 }, 8, 0x5a4436);
  if (d >= 2) {
    cracks(g, rng, 132, 140, 26, { branches: 3, glow: P.moltenHot, width: 2 });
    // Molten leak drip.
    const drip = new Path2D();
    drip.moveTo(128, 150);
    drip.quadraticCurveTo(134, 166, 130, 176);
    drip.quadraticCurveTo(124, 166, 128, 150);
    emissive(g, drip, { x: 124, y: 150, w: 10, h: 26 }, P.moltenHot, { blur: 6 });
    scorch(g, 60, 90, 22, 0.55);
  }
}

function drawCrucibleGlow(g: G): void {
  glowMask(g, ellipse(100, 41, 74, 6), 18);
  glowMask(g, poly([
    [178, 38],
    [196, 32],
    [194, 37],
    [180, 42],
  ]), 8);
}

function drawHeart(g: G): void {
  softDot(g, 40, 40, 38, P.molten, 0.6);
  sphere(g, 40, 40, 27, 0xfffbe6, 0xd2410c, { spec: 0.7 });
  g.strokeStyle = css(mix(P.molten, 0xffffff, 0.4), 0.8);
  g.lineWidth = 1.8;
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2;
    g.beginPath();
    g.arc(40, 40, 16, a, a + 1.4);
    g.stroke();
  }
  g.fillStyle = 'rgba(255,255,255,0.95)';
  g.beginPath();
  g.arc(40, 40, 6, 0, Math.PI * 2);
  g.fill();
}

function drawLid(g: G, left: boolean): void {
  const cx = left ? 53 : 3;
  const p = new Path2D();
  if (left) {
    p.moveTo(cx, 6);
    p.arc(cx, 44, 38, -Math.PI / 2, Math.PI / 2, true);
    p.closePath();
  } else {
    p.moveTo(cx, 6);
    p.arc(cx, 44, 38, -Math.PI / 2, Math.PI / 2);
    p.closePath();
  }
  solid(g, p, { x: left ? 15 : 3, y: 6, w: 38, h: 76 }, P.plate, { bevel: 2.5 });
  const band = left ? chamfer(18, 38, 32, 12, 2) : chamfer(6, 38, 32, 12, 2);
  stripes(g, band, { x: left ? 18 : 6, y: 38, w: 32, h: 12 }, P.paint, P.stripe, 5);
  rivets(g, left
    ? [
        [42, 16],
        [42, 72],
        [24, 44],
      ]
    : [
        [14, 16],
        [14, 72],
        [32, 44],
      ], P.plate, 2);
}

// ───────────── crane / grapple ─────────────

function drawBoom(g: G): void {
  const top = chamfer(16, 8, 186, 8, 2);
  const bot = chamfer(16, 32, 186, 8, 2);
  // Braces first so the rails overlap them.
  g.strokeStyle = css(COLORS.ink);
  g.lineWidth = 6;
  const brace = new Path2D();
  for (let x = 24; x < 196; x += 24) {
    brace.moveTo(x, 14);
    brace.lineTo(x + 12, 34);
    brace.lineTo(x + 24, 14);
  }
  g.stroke(brace);
  g.strokeStyle = css(P.joint);
  g.lineWidth = 3;
  g.stroke(brace);
  solid(g, top, { x: 16, y: 8, w: 186, h: 8 }, P.paint, { bevel: 1.5 });
  solid(g, bot, { x: 16, y: 32, w: 186, h: 8 }, P.paint, { bevel: 1.5 });
  stripes(g, top, { x: 16, y: 8, w: 186, h: 8 }, P.paint, P.stripe, 10);
  hub(g, 196, 24, 12, P.joint, 4);
  hub(g, 16, 24, 15, P.joint, 6);
}

function drawHook(g: G): void {
  hub(g, 35, 8, 7, P.joint, 0);
  recess(g, disc(35, 8, 3), 0x060504, 1);
  const talon = (pts: Pt[]): void => solid(g, poly(pts), bounds(pts), P.steel, { bevel: 1.5, hi: 1.5 });
  talon([
    [16, 30],
    [26, 32],
    [20, 56],
    [28, 74],
    [12, 66],
    [8, 44],
  ]);
  talon([
    [54, 30],
    [44, 32],
    [50, 56],
    [42, 74],
    [58, 66],
    [62, 44],
  ]);
  talon([
    [30, 34],
    [40, 34],
    [40, 64],
    [35, 78],
    [30, 64],
  ]);
  const block = chamfer(16, 14, 38, 24, 4);
  solid(g, block, { x: 16, y: 14, w: 38, h: 24 }, P.hull, { bevel: 2 });
  stripes(g, chamfer(20, 26, 30, 8, 1), { x: 20, y: 26, w: 30, h: 8 }, P.paint, P.stripe, 4);
  emissiveDot(g, 35, 20, 3, COLORS.threatHot, 6);
}

function drawChain(g: G): void {
  g.strokeStyle = css(COLORS.ink);
  g.lineWidth = 5;
  g.strokeRect(5, 1, 6, 12);
  g.strokeStyle = css(P.steel);
  g.lineWidth = 2.5;
  g.strokeRect(5, 1, 6, 12);
  const link = chamfer(6, 13, 4, 12, 1);
  solid(g, link, { x: 6, y: 13, w: 4, h: 12 }, P.steel, { bevel: 0.5, outlineWidth: 1.5 });
}

// ───────────── hammer ─────────────

function drawPiston(g: G): void {
  const grad = g.createLinearGradient(4, 0, 28, 0);
  grad.addColorStop(0, css(COLORS.ink));
  grad.addColorStop(0.12, css(0x8d98a6));
  grad.addColorStop(0.32, css(0xe6edf5));
  grad.addColorStop(0.6, css(0x7a8494));
  grad.addColorStop(0.88, css(0x3a414c));
  grad.addColorStop(1, css(COLORS.ink));
  g.fillStyle = grad;
  g.fillRect(5, 0, 22, 48);
  g.fillStyle = css(COLORS.ink);
  g.fillRect(3, 0, 2, 48);
  g.fillRect(27, 0, 2, 48);
  g.fillStyle = css(0x2a2f37);
  g.fillRect(4, 0, 24, 4);
  g.fillStyle = 'rgba(255,255,255,0.25)';
  g.fillRect(4, 4, 24, 1);
}

function drawHammer(g: G): void {
  const socket = chamfer(54, 0, 40, 24, 4);
  solid(g, socket, { x: 54, y: 0, w: 40, h: 24 }, P.joint, { bevel: 2 });
  const head = chamfer(10, 20, 128, 54, [10, 10, 4, 4]);
  solid(g, head, { x: 10, y: 20, w: 128, h: 54 }, P.hull, { bevel: 3, hi: 1.5 });
  stripes(g, chamfer(16, 32, 116, 14, 2), { x: 16, y: 32, w: 116, h: 14 }, P.paint, P.stripe, 9);
  groove(g, [
    [18, 56],
    [130, 56],
  ], P.hull);
  rivets(g, [
    [20, 26],
    [128, 26],
    [20, 66],
    [128, 66],
  ], P.hull, 2.2);
  const face = chamfer(6, 70, 136, 18, [2, 2, 6, 6]);
  solid(g, face, { x: 6, y: 70, w: 136, h: 18 }, P.frame, { bevel: 2 });
  emissive(g, chamfer(10, 84, 128, 3, 1), { x: 10, y: 84, w: 128, h: 3 }, P.molten, { blur: 7 });
}

function drawHammerGlow(g: G): void {
  glowMask(g, chamfer(10, 82, 128, 6, 2), 12);
}

function drawNozzle(g: G): void {
  const barrel = chamfer(14, 12, 54, 20, [2, 4, 4, 2]);
  solid(g, barrel, { x: 14, y: 12, w: 54, h: 20 }, P.joint, { bevel: 2 });
  for (let x = 26; x < 60; x += 8) {
    const fin = chamfer(x, 8, 4, 28, 1);
    solid(g, fin, { x, y: 8, w: 4, h: 28 }, P.frame, { bevel: 0.5, outlineWidth: 1.5 });
  }
  const tip = chamfer(64, 8, 12, 28, 3);
  solid(g, tip, { x: 64, y: 8, w: 12, h: 28 }, P.steel, { bevel: 1.5 });
  recess(g, ellipse(76, 22, 3, 9), 0x070504, 1);
  emissiveDot(g, 76, 22, 3, P.moltenHot, 6);
  hub(g, 14, 22, 12, P.hull, 4);
}

/** All FOUNDRY TITAN textures. */
export function buildTitanArt(scene: Phaser.Scene): void {
  const T = TITAN_RIG.tex;
  for (const d of [0, 1, 2] as Dmg[]) {
    canvasTex(scene, TITAN_TEX.hull(d), T.hull.w, T.hull.h, (g) => drawHull(g, d));
    canvasTex(scene, TITAN_TEX.crucible(d), T.crucible.w, T.crucible.h, (g) => drawCrucible(g, d));
  }
  canvasTex(scene, TITAN_TEX.hullGlow, T.hull.w, T.hull.h, drawHullGlow);
  canvasTex(scene, TITAN_TEX.thruster, T.thruster.w, T.thruster.h, drawThruster);
  canvasTex(scene, TITAN_TEX.flame, T.flame.w, T.flame.h, drawFlame);
  canvasTex(scene, TITAN_TEX.crucibleGlow, T.crucible.w, T.crucible.h, drawCrucibleGlow);
  canvasTex(scene, TITAN_TEX.heart, T.heart.w, T.heart.h, drawHeart);
  canvasTex(scene, TITAN_TEX.heartGlow, T.heartGlow.w, T.heartGlow.h, (g) => {
    softDot(g, 110, 110, 110, 0xffffff, 0.9);
    softDot(g, 110, 110, 44, 0xffffff, 1);
  });
  canvasTex(scene, TITAN_TEX.lidL, T.lidL.w, T.lidL.h, (g) => drawLid(g, true));
  canvasTex(scene, TITAN_TEX.lidR, T.lidR.w, T.lidR.h, (g) => drawLid(g, false));
  canvasTex(scene, TITAN_TEX.boom, T.boom.w, T.boom.h, drawBoom);
  canvasTex(scene, TITAN_TEX.hook, T.hook.w, T.hook.h, drawHook);
  canvasTex(scene, TITAN_TEX.chain, T.chain.w, T.chain.h, drawChain);
  canvasTex(scene, TITAN_TEX.piston, T.piston.w, T.piston.h, drawPiston);
  canvasTex(scene, TITAN_TEX.hammer, T.hammer.w, T.hammer.h, drawHammer);
  canvasTex(scene, TITAN_TEX.hammerGlow, T.hammer.w, T.hammer.h, drawHammerGlow);
  canvasTex(scene, TITAN_TEX.nozzle, T.nozzle.w, T.nozzle.h, drawNozzle);
}
